import { useMemo, useEffect } from 'react';
import { useNearViewport } from '@shared/hooks/useNearViewport';
import { useAccommodationRedirectUrl, createUtmParametersForAccommodationsConsolePage } from '@apps/stays/AccommodationsConsole/hooks/useAccommodationRedirectUrl';
import { useAccommodationsListingMobileLayout } from '@apps/stays/AccommodationsConsole/hooks/useAccommodationsListingMobileLayout';
import { useListingsPanelTelemetry } from '@apps/stays/AccommodationsConsole/components/ListingsPanel/ListingsPanel.telemetry';
import { useFeaturedPlacementTelemetry } from '@apps/stays/AccommodationsConsole/FeaturedPlacementTelemetryContext';
import { useSafeUrlParser } from '@shared/hooks/useSafeUrlParser';
import { useAcquisitionAwareUtm } from '@shared/hooks/useAcquisitionAwareUtm';
import { getSelectedRedirectTelemetryName, getSelectedRedirectTravelOfferProvider } from '@shared/utils/extractHotelRedirects';
import { getValidCoordinates } from '@shared/core/places';
import { useTranslation } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { withWindow } from '@shared/utils/withWindow';
import { affiliateHandoffTelemetry } from '@shared/utils/formatting/affiliateHandoff';
import { otaTransferredPricingTelemetry } from '@apps/stays/AccommodationsConsole/components/ListingsPanel/otaTransferredPricing.telemetry';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { HotelSearchSource } from '@shared/constants/accommodations';
import type { ScenarioListingEntryProps, ListingEntryBase } from './ListingEntry.types';

// Shared hook for all scenario listing entries.
// Computes redirect URL, telemetry handlers, point-of-interest, sold-out state,
// and mobile layout — everything that is identical across Guest/Admin/OTA.
export const useListingEntryBase = ({ listing, state, noOfNights, sourceName }: ScenarioListingEntryProps): ListingEntryBase => {
  const { shouldUseNewMobileLayout, isMobileOrTablet } = useAccommodationsListingMobileLayout();
  const isDesktopLayout = !isMobileOrTablet;
  const getRedirectUrl = useAccommodationRedirectUrl(state, sourceName);

  const isSoldOut = listing.type === 'standardHotel' && !!listing.isSoldOutForSelectedDates;

  // Treatment lets a sold-out tile open the PDP. Scoped to the guest/admin search view so only those tiles are bucketed.
  const isSoldOutPdpScopedSource = sourceName === HotelSearchSource.GUEST_MAP || sourceName === HotelSearchSource.ADMIN_MAP;
  const [isSoldOutPdpEnabled] = useIsFeatureEnabled('accommodationsSoldOutPdpExperiment', { skip: !isSoldOut || !isSoldOutPdpScopedSource });
  const isLinkEnabled = !isSoldOut || isSoldOutPdpEnabled;

  // Treatment routes a sold-out tile to the hotel PDP by skipping the interceptor on the redirect page.
  const redirectUrl = getRedirectUrl(listing, { skipAvailabilityCheck: isSoldOut && isSoldOutPdpEnabled });
  const telemetry = useListingsPanelTelemetry();
  // Read, never resolved here: every scenario tile runs this hook, so re-reading the experiment flag
  // would fire one Amplitude exposure per rendered tile.
  const featuredPlacementTelemetry = useFeaturedPlacementTelemetry();
  const { tq } = useTranslation('accommodationsConsole');
  const parseUrl = useSafeUrlParser();
  // Same resolution the redirect URL uses, so the Amplitude event and the URL can't disagree.
  const mergedUtm = useAcquisitionAwareUtm(createUtmParametersForAccommodationsConsolePage(sourceName));

  const selectedRedirectTelemetryName = useMemo(() => getSelectedRedirectTelemetryName(listing.redirectMechanisms), [listing.redirectMechanisms]);

  const { ref: containerRef, hasBeenNear } = useNearViewport({ preloadPixels: 0 });

  useEffect(() => {
    if (hasBeenNear && listing.isSponsored) {
      // Same attribution as the click events below, so impressions and clicks divide into a real
      // click-through rate instead of two counts that cannot be compared across arms.
      telemetry.promotedHotelImpression({ joyPlaceId: listing.joyPlaceId || '', hotelName: listing.name, chain: listing.chain, ...featuredPlacementTelemetry });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasBeenNear]);

  const handleSeeAvailability = useEventCallback(() => {
    telemetry.seeAvailabilityClicked({
      joyPlaceId: listing.joyPlaceId || '',
      hotelName: listing.name,
      isSponsored: listing.isSponsored ?? false,
      isDirectBooking: listing.isDirectBooking ?? false,
      chain: listing.chain,
      isCoupleFavourite: listing.isFavorite ?? false,
      priceSource: listing.pricingInfo.source,
      travelOfferProvider: getSelectedRedirectTravelOfferProvider(listing.redirectMechanisms),
      ...otaTransferredPricingTelemetry(listing),
      ...featuredPlacementTelemetry,
    });

    if (redirectUrl) {
      const urlInfo = parseUrl(redirectUrl, 'ListingEntry.handleSeeAvailability');
      if (urlInfo) {
        telemetry.hotelBookingRedirect({
          joyPlaceId: listing.joyPlaceId || '',
          hotelName: listing.name,
          destinationURL: redirectUrl,
          destinationDomain: urlInfo.hostname,
          destinationName: selectedRedirectTelemetryName,
          isSponsored: listing.isSponsored ?? false,
          isDirectBooking: listing.isDirectBooking ?? false,
          chain: listing.chain,
          isCoupleFavourite: listing.isFavorite ?? false,
          // Read back off the finished URL rather than from our intent when we built it, so a call
          // site that stops wrapping shows up in the data instead of reporting what it no longer
          // does. `destinationDomain` above is the Joy wrap host for anything sponsored; the
          // advertiser the click actually reaches only exists in `advertiserDomain`.
          ...affiliateHandoffTelemetry(redirectUrl),
          ...otaTransferredPricingTelemetry(listing),
          ...mergedUtm,
          ...featuredPlacementTelemetry,
        });
      }
    }
  });

  const handlePhotoClick = useEventCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const isCarouselControl = target.closest('button, [role="button"], [data-carousel-control]');
    if (!isCarouselControl && isLinkEnabled && redirectUrl) {
      handleSeeAvailability();
      withWindow(global => global.open(redirectUrl, '_blank'));
    }
  });

  const pointOfInterest = useMemo(() => {
    const location = getValidCoordinates(state.circularRegionSearch);
    if (!location) return null;

    let displayName = state.searchLocation || tq`venue`;
    if (state.searchLocation === state.venue.name) displayName = tq`venue`;

    return { displayName, location };
  }, [state.circularRegionSearch, state.searchLocation, state.venue.name, tq]);

  return {
    listing,
    state,
    noOfNights,
    sourceName,
    redirectUrl,
    isSoldOut,
    isLinkEnabled,
    isMobileOrTablet,
    isDesktopLayout,
    shouldUseNewMobileLayout,
    pointOfInterest,
    handleSeeAvailability,
    handlePhotoClick,
    containerRef,
  };
};
